/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { Input, InputGroup, InputGroupText } from 'reactstrap';
import { formatCalculationNumber } from '../../../../../../utils/formula';
import { ColumnInputProps } from '../../InputProps';
import { getSingleRowTableAddon } from '../tableUtils';
import { InputTableGroupPrePend, TableInputGroupAppend } from '@features/survey/form/input/table/TableInputGroup';
import { InputHelperText } from '@features/survey/form/input/InputHelperText';
import { validateCalculatedColumn } from '@features/survey/form/input/table/calculation';

export default function CalculatedInput(props: ColumnInputProps) {
  const {
    column,
    inputColumn,
    placeholder,
    label,
    universalTracker,
  } = props;

  const rawUtr = universalTracker.getRaw();
  const value = formatCalculationNumber(inputColumn.value ?? '');
  const { required, max, min } = column.validation ?? {};

  const floatVal = parseFloat(value.replace(/,/g, ''));
  const isLessThanMax = max === undefined || floatVal <= max;
  const isMoreThanMin = min === undefined || floatVal >= min;
  const isValid = value === '' || (isLessThanMax && isMoreThanMin);

  const { beforeAddon, afterAddon, belowInputAddon } = getSingleRowTableAddon({ ...props, columnCode: column.code });

  const tableColumns = universalTracker.getValueValidation()?.table?.columns ?? [];
  const warningMessage =
    column && !validateCalculatedColumn(tableColumns, column) ? (
      <span data-testid='unit-type-mismatch-helper-text'>
        This column uses the <strong>{column.unitType}</strong> unit, but the variables it references use different unit
        types.
      </span>
    ) : undefined;

  return (
    <div className='mb-3 auto-calculation'>
      {label}
      <InputGroup className='position-relative w-100'>
        {beforeAddon?.element || null}
        {InputTableGroupPrePend({ ...props, isDisabled: () => true })}
        {!isValid ? (
          <InputGroupText className='styled-readonly is-invalid'>
            <i className='fa fa-exclamation-triangle text-ThemeDangerMedium' />
          </InputGroupText>
        ) : null}
        <Input
          autoComplete='off'
          type='text'
          max={max}
          min={min}
          invalid={!isValid}
          className='styled-readonly'
          id={column.code}
          name={column.code}
          value={value}
          placeholder={placeholder}
          disabled={true}
          required={required !== false}
        />
        {TableInputGroupAppend({ ...props, isDisabled: () => true })}
        <InputHelperText
          invalidMessage={''}
          universalTracker={rawUtr}
          columnCode={column.code}
          warningMessage={warningMessage}
        />
        {afterAddon?.element || null}
      </InputGroup>
      {belowInputAddon?.element || null}
    </div>
  );
}
